import { LitElement } from "lit"
import { customElement, property, state } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import along from "@turf/along"
import bearing from "@turf/bearing"
import bezierSpline from "@turf/bezier-spline"
import length from "@turf/length"
import lineSliceAlong from "@turf/line-slice-along"
import { lineString } from "@turf/helpers"
import type { MapControl, MapControlPosition } from "../map-control.ts"
import { STANDARD_OVERLAY_SLOT } from "../map-styles.ts"
import { iconMarkup, type IconName } from "@/components/Icon.tsx"

type Position = number[]
interface LineString {
  type: "LineString"
  coordinates: Position[]
}
interface Feature<G> {
  type: "Feature"
  geometry: G
  properties: Record<string, unknown> | null
}

const ROUTE_SOURCE_ID = "trip-route"
const TRAVERSED_SOURCE_ID = "route-anim-traversed"
const TRAVERSED_LAYER_ID = "route-anim-traversed-line"
const CURSOR_SOURCE_ID = "route-anim-cursor"
const CURSOR_LAYER_ID = "route-anim-cursor-point"
const DEFAULT_DURATION_S = 30
const BEARING_SMOOTH = 0.1
const TRAVERSED_COLOR = "#ef4444"
const CURSOR_COLOR = "#ef4444"

function lerp(a: number, b: number): number {
  return (1 - BEARING_SMOOTH) * a + BEARING_SMOOTH * b
}

/**
 * RouteAnimatorControl — Play/Pause/Reset for a camera-follow animation along
 * the trip route. Reads the geojson from the `trip-route` source (supports
 * both LineString and MultiLineString — multi-segments are concatenated).
 *
 * Implementation mirrors fotolog/apps/web/src/lib/geo.ts:
 *   - bezier-spline the path for smoother camera motion
 *   - sample with @turf/along by elapsed-time fraction
 *   - rotate camera bearing using a periodic update + per-frame lerp
 */
@customElement("route-animator-control")
export class RouteAnimatorControl extends LitElement implements MapControl {
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "position" })
  accessor position: MapControlPosition = "top-right"

  @property({ type: Number, attribute: "duration" })
  accessor duration: number = DEFAULT_DURATION_S

  @property({ type: Boolean, attribute: "rotate" })
  accessor rotate: boolean = true

  /**
   * Debug only: render the traversed-line/cursor along the bezier-smoothed
   * camera path instead of the actual route. Off by default — the visible dot
   * follows the real GPX coordinates.
   */
  @property({ type: Boolean, attribute: "debug-smooth-progress" })
  accessor debugSmoothProgress: boolean = false

  @state()
  private accessor _playing = false

  @state()
  private accessor _hasRoute = false

  private _map: mapboxgl.Map | null = null
  private _container: HTMLElement | null = null

  private _smoothLine: Feature<LineString> | null = null
  private _smoothLengthM = 0
  private _rawLine: Feature<LineString> | null = null
  private _rawLengthM = 0

  private _rafId: number | null = null
  private _startTimeMs: number | null = null
  private _elapsedAtPauseS = 0
  private _lastBearing: number | null = null

  // Saved camera state to restore on reset/stop.
  private _savedCamera: {
    center: mapboxgl.LngLat
    zoom: number
    bearing: number
    pitch: number
  } | null = null

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map
    this._container = document.createElement("div")
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group"
    this._container.style.cssText = "display: flex;"

    // Re-check the route whenever the source data changes.
    map.on("sourcedata", this._onSourceData)
    this._loadRoute()
    this._render()
    return this._container
  }

  onRemove(_map: mapboxgl.Map): void {
    this._stop()
    if (this._map) {
      this._map.off("sourcedata", this._onSourceData)
      if (this._map.getLayer(TRAVERSED_LAYER_ID)) {
        this._map.removeLayer(TRAVERSED_LAYER_ID)
      }
      if (this._map.getSource(TRAVERSED_SOURCE_ID)) {
        this._map.removeSource(TRAVERSED_SOURCE_ID)
      }
      if (this._map.getLayer(CURSOR_LAYER_ID)) {
        this._map.removeLayer(CURSOR_LAYER_ID)
      }
      if (this._map.getSource(CURSOR_SOURCE_ID)) {
        this._map.removeSource(CURSOR_SOURCE_ID)
      }
    }
    this._container?.remove()
    this._container = null
    this._map = null
  }

  private _onSourceData = (e: mapboxgl.MapSourceDataEvent) => {
    if (e.sourceId === ROUTE_SOURCE_ID && e.isSourceLoaded) this._loadRoute()
  }

  private _loadRoute() {
    if (!this._map) return
    const src = this._map.getSource(ROUTE_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (!src) {
      this._setHasRoute(false)
      return
    }
    // deno-lint-ignore no-explicit-any
    const data = (src as any)._data
    const segments = extractSegments(data)
    if (segments.length === 0 || segments.every((s) => s.length < 2)) {
      this._setHasRoute(false)
      return
    }

    // Concatenate all segments into one polyline for a single journey.
    const flat: Position[] = segments.flat()
    try {
      this._rawLine = lineString(flat)
      this._rawLengthM = length(this._rawLine, { units: "meters" })
      this._smoothLine = bezierSpline(this._rawLine, { resolution: 99 })
      this._smoothLengthM = length(this._smoothLine, { units: "meters" })
      this._setHasRoute(this._smoothLengthM > 0 && this._rawLengthM > 0)
    } catch {
      this._rawLine = null
      this._rawLengthM = 0
      this._smoothLine = null
      this._smoothLengthM = 0
      this._setHasRoute(false)
    }
  }

  private _setHasRoute(v: boolean) {
    if (this._hasRoute !== v) {
      this._hasRoute = v
      this._render()
    }
  }

  private _render() {
    if (!this._container) return
    this._container.innerHTML = ""

    const makeBtn = (
      iconName: IconName,
      title: string,
      onClick: () => void,
      disabled = false,
    ) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.title = title
      btn.setAttribute("aria-label", title)
      btn.disabled = disabled
      btn.style.cssText =
        "padding: 6px 10px; font-size: 14px; border: none; background: #fff; color: #374151; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;"
      if (disabled) {
        btn.style.opacity = "0.4"
        btn.style.cursor = "not-allowed"
      }
      btn.insertAdjacentHTML("beforeend", iconMarkup(iconName))
      btn.addEventListener("click", onClick)
      return btn
    }

    const disabled = !this._hasRoute
    this._container.appendChild(
      this._playing
        ? makeBtn("stop-fill", "Stop Animation", () => this._reset(), disabled)
        : makeBtn("play-fill", "Animate Route", () => this._play(), disabled),
    )
  }

  private _play() {
    if (!this._map || !this._smoothLine || this._smoothLengthM <= 0) return

    if (!this._savedCamera) {
      this._savedCamera = {
        center: this._map.getCenter(),
        zoom: this._map.getZoom(),
        bearing: this._map.getBearing(),
        pitch: this._map.getPitch(),
      }
    }

    this._ensureLayers()

    this._playing = true
    this._startTimeMs = null // computed on first frame from _elapsedAtPauseS
    this._render()
    this._rafId = requestAnimationFrame(this._frame)
  }

  private _ensureLayers() {
    if (!this._map) return
    if (!this._map.getSource(TRAVERSED_SOURCE_ID)) {
      this._map.addSource(TRAVERSED_SOURCE_ID, {
        type: "geojson",
        data: { type: "LineString", coordinates: [] },
      })
      this._map.addLayer({
        id: TRAVERSED_LAYER_ID,
        type: "line",
        source: TRAVERSED_SOURCE_ID,
        slot: STANDARD_OVERLAY_SLOT,
        layout: { "line-join": "round", "line-cap": "round" },
        paint: { "line-color": TRAVERSED_COLOR, "line-width": 4 },
      })
    }
    if (!this._map.getSource(CURSOR_SOURCE_ID)) {
      this._map.addSource(CURSOR_SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      })
      this._map.addLayer({
        id: CURSOR_LAYER_ID,
        type: "circle",
        source: CURSOR_SOURCE_ID,
        slot: STANDARD_OVERLAY_SLOT,
        paint: {
          "circle-radius": 7,
          "circle-color": CURSOR_COLOR,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      })
    }
  }

  private _updateProgress(phase: number) {
    if (!this._map) return
    const line = this.debugSmoothProgress ? this._smoothLine : this._rawLine
    const lengthM = this.debugSmoothProgress ? this._smoothLengthM : this._rawLengthM
    if (!line || lengthM <= 0) return

    const travelledM = Math.max(0, Math.min(1, phase)) * lengthM
    const point = along(line, travelledM, { units: "meters" })
      .geometry.coordinates

    const traversed = this._map.getSource(TRAVERSED_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    if (traversed) {
      const data: LineString = travelledM > 0
        ? lineSliceAlong(line, 0, travelledM, { units: "meters" }).geometry
        : { type: "LineString", coordinates: [] }
      traversed.setData(data)
    }

    const cursor = this._map.getSource(CURSOR_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    cursor?.setData({
      type: "Feature",
      geometry: { type: "Point", coordinates: [point[0]!, point[1]!] },
      properties: {},
    })
  }

  private _clearProgress() {
    if (!this._map) return
    const traversed = this._map.getSource(TRAVERSED_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    traversed?.setData({ type: "LineString", coordinates: [] })
    const cursor = this._map.getSource(CURSOR_SOURCE_ID) as
      | mapboxgl.GeoJSONSource
      | undefined
    cursor?.setData({ type: "FeatureCollection", features: [] })
  }

  private _frame = (frameTimeMs: number) => {
    if (!this._map || !this._smoothLine) return

    if (this._startTimeMs === null) {
      this._startTimeMs = frameTimeMs - this._elapsedAtPauseS * 1000
    }
    const elapsedS = (frameTimeMs - this._startTimeMs) / 1000
    const phase = elapsedS / this.duration

    if (phase >= 1) {
      const camEnd = along(this._smoothLine, this._smoothLengthM, {
        units: "meters",
      }).geometry.coordinates
      this._map.setCenter([camEnd[0]!, camEnd[1]!])
      this._updateProgress(1)
      this._stop()
      return
    }

    const camTravelled = phase * this._smoothLengthM
    const camPoint = along(this._smoothLine, camTravelled, { units: "meters" })
      .geometry.coordinates
    // setCenter preserves the user's pitch, zoom, and bearing — in 3D mode
    // the user can pan/pitch/zoom mid-animation to adjust for terrain
    // occlusion. setBearing below is the only other camera mutation.
    this._map.setCenter([camPoint[0]!, camPoint[1]!])
    this._updateProgress(phase)

    if (this.rotate) {
      const lookAhead = Math.min(
        camTravelled + this._smoothLengthM / 200,
        this._smoothLengthM,
      )
      const next = along(this._smoothLine, lookAhead, { units: "meters" })
      // +360 to avoid negative angles confusing the lerp at the dateline.
      const target = bearing(camPoint, next) + 360
      if (this._lastBearing === null) this._lastBearing = target
      this._lastBearing = lerp(this._lastBearing, target)
      this._map.setBearing(this._lastBearing % 360)
    }

    this._rafId = requestAnimationFrame(this._frame)
  }

  private _stop() {
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
    this._playing = false
    this._startTimeMs = null
    this._render()
  }

  private _reset() {
    this._stop()
    this._elapsedAtPauseS = 0
    this._lastBearing = null
    this._clearProgress()
    if (this._map && this._savedCamera) {
      this._map.easeTo({
        center: this._savedCamera.center,
        zoom: this._savedCamera.zoom,
        bearing: this._savedCamera.bearing,
        pitch: this._savedCamera.pitch,
        duration: 600,
      })
    }
    this._savedCamera = null
  }
}

function extractSegments(data: unknown): [number, number][][] {
  if (!data || typeof data !== "object") return []
  const d = data as { type?: string; coordinates?: unknown }
  if (d.type === "MultiLineString" && Array.isArray(d.coordinates)) {
    return d.coordinates as [number, number][][]
  }
  if (d.type === "LineString" && Array.isArray(d.coordinates)) {
    return [d.coordinates as [number, number][]]
  }
  return []
}

declare global {
  interface HTMLElementTagNameMap {
    "route-animator-control": RouteAnimatorControl
  }
}
